import { FC, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { IconMenu2, IconX } from '@tabler/icons-react';
import PartnerSidebar from './PartnerSidebar';
import useGetPartner from '../../hooks/useGetPartner';
import useIsAdmin from '../../hooks/useIsAdmin';
import { CircularProgress, Alert } from '@mui/material';
import { getErrorMessage } from '../../utils/errorUtils';

const PartnerLayout: FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const {
    partner,
    isLoading: partnerLoading,
    isError: partnerError,
    error: partnerErrorDetails,
  } = useGetPartner();
  const { isAdmin, isLoading: adminLoading } = useIsAdmin();

  // Show loading state while fetching partner/admin data
  if (partnerLoading || adminLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <CircularProgress />
      </div>
    );
  }

  // Check if user doesn't have a partner account (but allow admins)
  const hasNoPartner = !partnerLoading && !partner && !partnerError;
  if (hasNoPartner && !isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 lg:p-12">
        <div className="mx-auto max-w-7xl">
          <Alert severity="info" className="mb-4">
            <div className="font-semibold">No Partner Account Found</div>
            <div className="mt-2 text-sm">
              You are authenticated but don't have a partner account associated
              with your user.
            </div>
            <div className="mt-2 text-xs text-gray-600">
              Please contact support to set up your partner account.
            </div>
          </Alert>
        </div>
      </div>
    );
  }

  // Show error state
  if (partnerError) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 lg:p-12">
        <div className="mx-auto max-w-7xl">
          <Alert severity="error" className="mb-4">
            <div className="font-semibold">Error loading partner data</div>
            <div className="mt-2 text-sm">
              {getErrorMessage(partnerErrorDetails)}
            </div>
            <div className="mt-2 text-xs text-gray-600">
              Please try refreshing the page or contact support if the issue
              persists.
            </div>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <PartnerSidebar
        partner={partner}
        isAdmin={isAdmin}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Main content area */}
      <div className="flex flex-1 flex-col lg:pl-64">
        {/* Mobile header */}
        <div className="sticky top-0 z-30 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3 lg:hidden">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setSidebarOpen(!sidebarOpen);
              }
            }}
            className="text-gray-600 hover:text-gray-900"
            aria-label="Toggle sidebar"
            aria-expanded={sidebarOpen}
            type="button"
            tabIndex={0}
          >
            {sidebarOpen ? <IconX size={24} /> : <IconMenu2 size={24} />}
          </button>
          <div className="flex-1 text-center">
            <h1 className="text-lg font-semibold text-gray-900">
              Partner Portal
            </h1>
          </div>
          <div className="w-6" /> {/* Spacer for centering */}
        </div>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default PartnerLayout;
